import React, { useEffect, useMemo, useState } from 'react'
import { Button, Grid, Heading, Icon, Text, useIsDesignLanguage, DesignLanguage, useToast, useDesignLanguagePerClass } from '@afterpaytouch/core'

import { Head, ModuleContainer } from '../../../src/component'
import { BrowserType, CardScanAmplitudeErrorReason, CardScanError, CheckoutError, CheckoutPage } from '../../../src/model'
import { Route, useSummaryRoute } from '../../../src/config/router'
import { PaymentMethodCard } from '../../../src/component/PaymentMethodCard'
import {
  confirmCheckout,
  initialiseScanIntent,
  setDisplayProcessingModal,
  startCardScan,
  useAppDispatch,
  useAppRouter,
  useIsReadyToScan,
  useSelectedCard,
} from '../../../src/state'
import { isMobileChrome, isMobileSafari, isMobileSamsung } from '../../../src/utils/device'
import { unwrapResult } from '@reduxjs/toolkit'
import { isPortalApiError } from '@afterpaytouch/portal-api'
import { TrackingEvent } from '../../../src/model/monitoring'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../../src/integrations/monitoring'
import { getConfirmCheckoutErrorHandling } from '../../../src/utils/redirects'
import { useTranslation } from '../../utils/convergence'
import styles from './style.module.css'

const errorExist = (error: CardScanError | undefined): error is CardScanError => Boolean(error)
const nonScanErrors: CardScanError[] = [CardScanError.Unsupported, CardScanError.NoPermission]
const isNonScanErrors = (error: CardScanError | undefined): boolean => nonScanErrors.includes(error as CardScanError)

const isTrue = (v: any): v is true => v === true

export const CardScanPage: CheckoutPage = () => {
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const designLanguageStyles = useDesignLanguagePerClass(styles)

  const dispatch = useAppDispatch()
  const [error, setError] = useState<CardScanError>()
  const [browserType, setBrowserType] = useState<BrowserType>(BrowserType.Others)
  const [clicked, setClicked] = useState<boolean>(false)
  const { t } = useTranslation()
  const router = useAppRouter()
  const ready = useIsReadyToScan()
  const selectedCard = useSelectedCard()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const summaryRoute = useSummaryRoute()
  const pageTitle = `${t('cardScan:title')} | ${t('common:brandName')}`
  const toast = useToast()

  const title: string = useMemo<string>(() => {
    if (errorExist(error)) {
      if (isNonScanErrors(error)) {
        return t(`cardScan:error.title.${CardScanError.NoPermission}`)
      }

      return t(`cardScan:error.title.${error}`)
    }

    return t('cardScan:title')
  }, [t, error])
  const subtitle = useMemo<string>(() => {
    if (errorExist(error)) {
      if (isNonScanErrors(error)) {
        return t(`cardScan:error.subtitle.${CardScanError.NoPermission}.${browserType}`)
      }

      const lastFour = selectedCard?.maskedPan.slice(-4)
      return t(`cardScan:error.subtitle.${error}`, { lastFour })
    }

    return t('cardScan:subtitle')
  }, [t, error, browserType, selectedCard])
  const backToReviewOrderBtnVisible: boolean = useMemo<boolean>(() => {
    return !isNonScanErrors(error)
  }, [error])
  const scanAgainBtnVisible: boolean = useMemo<boolean>(() => {
    return [CardScanError.DidntMatch, CardScanError.CannotScan].includes(error as CardScanError)
  }, [error])

  const handleBackToReviewOrder = (): void => {
    router.push(summaryRoute)
  }

  const initialise = async (): Promise<void> => {
    try {
      const initialiseScanIntentResult: true | CheckoutError = await dispatch(initialiseScanIntent())
      if (!isTrue(initialiseScanIntentResult) && initialiseScanIntentResult.error === CardScanError.Unsupported) {
        setError(CardScanError.Unsupported)
      }
    } catch (ignore) {
      router.push(Route.TERMINAL_ERROR)
    }
  }

  const handleScanClick = async (): Promise<void> => {
    setClicked(true)
    logEvent(TrackingEvent.PRESSED_SCAN_PAYMENT)
    const result: undefined | true | CheckoutError = await dispatch(startCardScan())

    if (isTrue(result)) {
      logEvent(TrackingEvent.SCAN_PAYMENT_MATCHED)
      try {
        const confirmResponse = unwrapResult(await dispatch(confirmCheckout()))
        confirmResponse.status === 'FAILURE' ? router.push(Route.TERMINAL_ERROR) : dispatch(setDisplayProcessingModal(true))
      } catch (e) {
        // wont trigger card scan twice
        capture.error('Error during payment confirmation', { ...e })
        if (isPortalApiError(e)) {
          const { route, messageKey } = getConfirmCheckoutErrorHandling({
            errorCode: e.errorCode,
            returnRoute: summaryRoute,
          })
          if (typeof messageKey === 'string') {
            toast({
              message: t(messageKey),
              kind: 'Failure',
            })
          }
          setClicked(false)
          router.push(route ?? Route.TERMINAL_ERROR)
        } else {
          capture.warning('Unexpected error during payment confirmation', { ...e })
        }
      }
    } else {
      setError(result?.error as CardScanError)
      switch (result?.error) {
        case CardScanError.UserCanceled: // just remove the card-scan verify screen
          setError(undefined)
          break
        case CardScanError.DidntMatch:
          logEvent(TrackingEvent.SCAN_PAYMENT_FAILED, {
            reason: CardScanAmplitudeErrorReason.NotMatched,
          })
          initialise()
          break
        case CardScanError.TooManyTries:
          logEvent(TrackingEvent.SCAN_PAYMENT_FAILED, {
            reason: CardScanAmplitudeErrorReason.RateLimit,
          })
          break
        case CardScanError.NoPermission:
        case CardScanError.CannotScan:
          logEvent(TrackingEvent.SCAN_PAYMENT_FAILED, {
            reason: CardScanAmplitudeErrorReason.OtherReasons,
          })
          break
        case CardScanError.TerminalError:
          setError(undefined)
          logEvent(TrackingEvent.SCAN_PAYMENT_FAILED, {
            reason: CardScanAmplitudeErrorReason.OtherReasons,
          })
          router.push(Route.TERMINAL_ERROR)
          break
      }
    }
    setClicked(false)
  }

  useEffect(() => {
    initialise()
    if (isMobileSamsung()) {
      setBrowserType(BrowserType.Samsung)
    } else if (isMobileChrome()) {
      setBrowserType(BrowserType.Chrome)
    } else if (isMobileSafari()) {
      setBrowserType(BrowserType.Safari)
    }
  }, [])

  const subtitleColor = isArcadeDesignLanguage ? 'Subtle' : undefined

  return (
    <>
      <Head title={pageTitle} />
      <Grid>
        <Heading size='XXL'>{title}</Heading>
      </Grid>
      <Grid>
        <Text size='M' color={subtitleColor}>
          {subtitle}
        </Text>
      </Grid>
      {!errorExist(error) || isArcadeDesignLanguage ? (
        <>
          {selectedCard != null && (
            <ModuleContainer
              padding={{ vertical: 'Large', horizontal: 'XLarge' }}
              data-dd-action-name='Card Scan Selected Card'
              className={designLanguageStyles.moduleContainer}
            >
              <PaymentMethodCard card={selectedCard} errorKey={selectedCard?.error ?? undefined} />
            </ModuleContainer>
          )}
          <Grid>
            <Text size='M' color='Gray40' className={designLanguageStyles.tips}>
              {t('cardScan:tips')}
            </Text>
          </Grid>
          {errorExist(error) && isArcadeDesignLanguage && (
            <>
              <Grid>
                <Grid.Item>
                  <Button.Secondary padding='Fluid' onClick={handleBackToReviewOrder} testNameSpace='cardScan'>
                    {t('cardScan:backToReviewOrder')}
                  </Button.Secondary>
                </Grid.Item>
              </Grid>
              <span className={designLanguageStyles.buttonSpacer}></span>
            </>
          )}
          <Grid>
            <Grid.Item>
              <Button padding='Fluid' onClick={handleScanClick} testNameSpace='cardScan' loading={clicked} disabled={!ready || clicked}>
                <Icon.Camera size='Small' title={t('cardScan:scan')} />
                {t('cardScan:scan')}
              </Button>
            </Grid.Item>
          </Grid>
        </>
      ) : (
        <>
          {backToReviewOrderBtnVisible && (
            <Grid>
              <Button padding='Fluid' onClick={handleBackToReviewOrder} testNameSpace='cardScan'>
                {t('cardScan:backToReviewOrder')}
              </Button>
            </Grid>
          )}
          {scanAgainBtnVisible && (
            <Grid>
              <Button.Ghost padding='Fluid' loading={clicked} onClick={handleScanClick} testNameSpace='cardScan' disabled={!ready || clicked}>
                <Icon.Camera size='Small' title={t('cardScan:scanAgain')} />
                {t('cardScan:scanAgain')}
              </Button.Ghost>
            </Grid>
          )}
        </>
      )}
      <TrackOnMount eventName={TrackingEvent.VIEWED_CARD_SCAN_PAGE} />
    </>
  )
}
